import { useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import {
    createDraftAtomEffect,
    createStepIfEmptyAtomEffect,
    draftStepAtom,
    editableStepsAtom,
    newStepAtom,
    stepIndexBeingEditedAtom,
} from "@/routes/automations/automation-details-page/state";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { AutomationStepEditArea } from "@/routes/automations/automation-edit-page/AutomationStepEditArea";
import { AutomationStepList } from "@/routes/automations/automation-details-page/AutomationStepList";
import { AutomationEmptyStepCreator } from "@/routes/automations/automation-edit-page/AutomationEmptyStepCreator";
import { StepListContainer } from "@/routes/automations/automation-details-page/StepListContainer";
import { cn } from "@/components/podkit/lib/cn";

const STEP_EDIT_AREA_WIDTH = 480;

export const AutomationDefinitionSectionEdit: FC = () => {
    useAtom(createStepIfEmptyAtomEffect);
    useAtom(createDraftAtomEffect);

    const [isClosingSidePanel, setIsClosingSidePanel] = useState(false);
    const editableSteps = useAtomValue(editableStepsAtom);
    const draftStep = useAtomValue(draftStepAtom);
    const isEditSidePanelVisible = draftStep !== null && !isClosingSidePanel;

    const onSidePanelCloseAnimationStart = useCallback(() => {
        setIsClosingSidePanel(true);
    }, []);

    const onSidePanelCloseAnimationEnd = useCallback(() => {
        setIsClosingSidePanel(false);
    }, []);

    if (editableSteps === null) {
        return null;
    }

    let sideContent: ReactNode | null = null;
    if (draftStep === null && !isClosingSidePanel) {
        sideContent = null;
    } else {
        sideContent = (
            <AnimatedAutomationStepEditArea
                panelWidth={STEP_EDIT_AREA_WIDTH}
                onCloseAnimationStart={onSidePanelCloseAnimationStart}
                onCloseAnimationEnd={onSidePanelCloseAnimationEnd}
            />
        );
    }

    return (
        <div
            className={cn(
                "grid grow grid-rows-1 overflow-hidden border-t border-border-subtle",
                "ease-out-expo transition-all duration-150",
                isEditSidePanelVisible ? "grid-cols-[1fr_480px]" : "grid-cols-[1fr_0px]",
            )}
        >
            <StepListContainer className="flex-[3] grow rounded-none border-0">
                {editableSteps.length === 0 ? (
                    <AutomationEmptyStepCreator />
                ) : (
                    <div className="flex w-full max-w-screen-xl grow flex-row items-start justify-center gap-4">
                        <AutomationStepList steps={editableSteps} />
                    </div>
                )}
            </StepListContainer>
            {sideContent}
        </div>
    );
};

const AnimatedAutomationStepEditArea: FC<{
    panelWidth: number;
    onCloseAnimationStart: () => void;
    onCloseAnimationEnd: () => void;
}> = ({ panelWidth, onCloseAnimationStart, onCloseAnimationEnd }) => {
    const editAreaRef = useRef<HTMLDivElement>(null);
    const startTime = useRef(-1);
    const endTime = useRef(-1);
    const setNewStepAtom = useSetAtom(newStepAtom);
    const setStepIndexBeingEditedAtom = useSetAtom(stepIndexBeingEditedAtom);
    const animationRequestIdRef = useRef<number | null>(null);

    const end = panelWidth + 100;

    const playAnimation = useCallback(
        ({ reverse = false }: { reverse?: boolean } = {}) =>
            new Promise<void>((resolve) => {
                const nextFrame = (timestamp: number) => {
                    if (!editAreaRef.current) return;

                    if (startTime.current === -1) {
                        if (reverse) {
                            onCloseAnimationStart();
                        }
                        startTime.current = timestamp;
                        endTime.current = timestamp + 400;
                    }

                    // t in [0, 1] representing the time progress of the animation
                    const t = (timestamp - startTime.current) / (endTime.current - startTime.current);
                    // p in [0, 1] representing the eased progress of the animation
                    // this is easeOutExpo (cubic-bezier(0.16, 1, 0.3, 1))
                    const p = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
                    // progress ranges from 0 to panelWidth
                    const progress = end * p;
                    const x = reverse ? progress : end - progress;

                    editAreaRef.current.style.transform = `translateX(${x}px)`;

                    if (t < 1) {
                        animationRequestIdRef.current = requestAnimationFrame(nextFrame);
                    } else {
                        startTime.current = -1;
                        endTime.current = -1;
                        onCloseAnimationEnd();
                        resolve();
                    }
                };
                animationRequestIdRef.current = requestAnimationFrame(nextFrame);
            }),
        [end, onCloseAnimationStart, onCloseAnimationEnd],
    );

    useEffect(() => {
        void playAnimation();
        return () => {
            if (animationRequestIdRef.current) {
                cancelAnimationFrame(animationRequestIdRef.current);
                animationRequestIdRef.current = null;
            }
        };
    }, [playAnimation]);

    const closeSidePanel = async () => {
        await playAnimation({ reverse: true });
        setNewStepAtom(null);
        setStepIndexBeingEditedAtom(null);
    };

    return <AutomationStepEditArea ref={editAreaRef} onClose={closeSidePanel} />;
};
